import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Download, AlertCircle, RefreshCw, Printer } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { Button } from '../../components/ui/button';
import pb from '../../lib/pocketbaseClient';
import InteriorQuotationPDF from '../../components/office/InteriorQuotationPDF';

const InteriorQuotationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const urlRef = useRef(null);

  // Convert any image (AVIF/WEBP/PNG/JPG) -> PNG base64 via canvas
  const convertToPng = (url) => new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 100;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        console.warn('[img-convert] canvas fail:', e);
        resolve(null);
      }
    };
    img.onerror = () => {
      console.warn('[img-convert] load fail:', url);
      resolve(null);
    };
    img.src = url;
  });

  const preloadImages = async (quotation) => {
    const items = Array.isArray(quotation?.items) ? quotation.items : [];
    const map = {};
    const tasks = [];
    for (const blk of items) {
      const prods = Array.isArray(blk.products) ? blk.products : (Array.isArray(blk.items) ? blk.items : []);
      for (const p of prods) {
        if (!p.image || !p.productId) continue;
        const key = p.productId;
        if (map[key] !== undefined) continue;
        const url = 'https://housedesigns99.com/api/files/interior_products/' + p.productId + '/' + p.image;
        tasks.push(
          convertToPng(url).then(png => { map[key] = png; })
        );
      }
    }
    await Promise.all(tasks);
    return map;
  };

  const generate = async () => {
    try {
      setLoading(true);
      setError(null);
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }

      const quotation = await pb.collection('interior_quotations').getOne(id, { expand: 'projectId,createdBy' });
      let project = quotation.expand?.projectId || null;
      if (!project && quotation.projectId) {
        try { project = await pb.collection('interior_projects').getOne(quotation.projectId); } catch (e) {}
      }
      let client = null;
      if (project?.clientId) {
        try { client = await pb.collection('leads').getOne(project.clientId); } catch (e) {}
      }

      // Preload all product images as base64
      const imageMap = await preloadImages(quotation);

      const blob = await pdf(
        <InteriorQuotationPDF
          quotation={quotation}
          project={project}
          client={client}
          imageMap={imageMap}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setPdfUrl(url);
    } catch (err) {
      console.error('[QuotationView] error:', err);
      setError(err.message || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }; }, [id]);

  const downloadPDF = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `Interior-Quotation-${id}.pdf`;
    a.click();
  };

  const printPDF = () => {
    if (!pdfUrl) return;
    const w = window.open(pdfUrl);
    if (w) w.print();
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-900 z-50">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-800 border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 text-slate-200 hover:text-white hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="text-slate-300 text-sm">
            <span className="font-semibold">Interior Quotation Preview</span>
            <span className="text-slate-500 font-mono text-xs ml-2">{id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={generate} disabled={loading} className="gap-1.5 text-slate-200 hover:text-white hover:bg-slate-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={printPDF} disabled={!pdfUrl} className="gap-1.5 text-slate-200 hover:text-white hover:bg-slate-700">
            <Printer className="w-4 h-4" /> Print
          </Button>
          <Button size="sm" onClick={downloadPDF} disabled={!pdfUrl} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download className="w-4 h-4" /> Download
          </Button>
        </div>
      </div>

      <div className="flex-1 relative bg-slate-700">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800 z-10">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mb-4" />
            <p className="text-sm text-slate-300">Generating interior quotation PDF…</p>
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="max-w-md bg-red-950 border border-red-700 rounded-xl p-6 text-center">
              <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
              <p className="text-red-200 font-bold mb-2">Cannot generate PDF</p>
              <p className="text-xs text-red-300 mb-4">{error}</p>
              <Button onClick={generate} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" /> Retry
              </Button>
            </div>
          </div>
        )}
        {pdfUrl && !loading && !error && (
          <iframe src={pdfUrl + '#view=FitH&toolbar=1'} title="Interior Quotation PDF" className="absolute inset-0 w-full h-full border-0" />
        )}
      </div>
    </div>
  );
};

export default InteriorQuotationView;
